import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Save, FolderOpen, Trash2, X, FileText, Clock, Download, Edit3, Check, XCircle } from 'lucide-react';
import './ProjectsPanel.css';
import logger from '../lib/logger';

// Use relative URL in production, env variable in development/preview
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ProjectsPanel = ({ onClose, onLoadProject, currentData, onSaveSuccess }) => {
  const { token, isAuthenticated } = useAuth();
  const [projects, setProjects] = useState([]);
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [activeTab, setActiveTab] = useState('projects');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingProject, setEditingProject] = useState(null);
  const [editName, setEditName] = useState('');

  const fetchData = useCallback(async () => {
    try {
      // Fetch projects
      const projectsResponse = await fetch(`${API_URL}/api/projects`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (projectsResponse.ok) {
        const projectsData = await projectsResponse.json();
        setProjects(projectsData);
      }

      // Fetch exports history
      const exportsResponse = await fetch(`${API_URL}/api/exports/history`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (exportsResponse.ok) {
        const exportsData = await exportsResponse.json();
        setExports(exportsData);
      }
    } catch (error) {
      logger.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleSaveProject = async () => {
    if (!projectName.trim()) {
      setError('Veuillez entrer un nom pour le projet');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: projectName,
          data: currentData
        })
      });

      if (response.ok) {
        setSuccess('Projet sauvegardé avec succès !');
        setProjectName('');
        fetchData();
        onSaveSuccess?.();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      setError('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadProject = (project) => {
    onLoadProject?.(project.data);
    setSuccess(`Projet "${project.name}" chargé !`);
    setTimeout(() => {
      setSuccess('');
      onClose?.();
    }, 1500);
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Supprimer le projet "${projectName}" ?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Projet supprimé');
        fetchData();
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (error) {
      setError('Erreur lors de la suppression');
    }
  };

  // Start editing a project
  const handleStartEdit = (project) => {
    setEditingProject(project.id);
    setEditName(project.name);
    setError('');
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingProject(null);
    setEditName('');
  };

  // Save project name edit
  const handleSaveEdit = async (projectId) => {
    if (!editName.trim()) {
      setError('Le nom du projet ne peut pas être vide');
      return;
    }

    try {
      const project = projects.find(p => p.id === projectId);
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editName,
          data: project.data
        })
      });

      if (response.ok) {
        setSuccess('Projet modifié avec succès');
        setEditingProject(null);
        setEditName('');
        fetchData();
        setTimeout(() => setSuccess(''), 2000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Erreur lors de la modification');
      }
    } catch (error) {
      setError('Erreur de connexion');
    }
  };

  // Update project with current form data
  const handleUpdateWithCurrentData = async (projectId, projectName) => {
    if (!window.confirm(`Mettre à jour "${projectName}" avec les données actuelles du formulaire ?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: projectName,
          data: currentData
        })
      });

      if (response.ok) {
        setSuccess('Projet mis à jour avec les données actuelles');
        fetchData();
        setTimeout(() => setSuccess(''), 2000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      setError('Erreur de connexion');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="projects-overlay" onClick={onClose}>
        <div className="projects-modal" onClick={(e) => e.stopPropagation()}>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
          <div className="not-authenticated">
            <FolderOpen size={48} />
            <h3>Connexion requise</h3>
            <p>Connectez-vous pour sauvegarder et gérer vos projets.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="projects-overlay" onClick={onClose}>
      <div className="projects-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}><X size={20} /></button>
        
        <h2 className="modal-title">
          <FolderOpen size={24} />
          Mes Projets
        </h2>

        {/* Tabs */}
        <div className="projects-tabs">
          <button 
            className={`tab-btn ${activeTab === 'projects' ? 'active' : ''}`}
            onClick={() => setActiveTab('projects')}
          >
            <FileText size={16} />
            Projets ({projects.length})
          </button>
          <button 
            className={`tab-btn ${activeTab === 'exports' ? 'active' : ''}`}
            onClick={() => setActiveTab('exports')}
          >
            <Download size={16} />
            Historique exports ({exports.length})
          </button>
        </div>

        {/* Messages */}
        {error && <div className="message error">{error}</div>}
        {success && <div className="message success">{success}</div>}

        {activeTab === 'projects' && (
          <>
            {/* Save new project */}
            <div className="save-section">
              <h3>Sauvegarder le projet actuel</h3>
              <div className="save-form">
                <input
                  type="text"
                  placeholder="Nom du projet (ex: Client ABC - Janvier 2025)"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSaveProject()}
                />
                <button onClick={handleSaveProject} disabled={saving} className="save-btn" data-testid="save-project-btn">
                  <Save size={18} />
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </div>
            </div>

            {/* Projects list */}
            <div className="projects-list">
              <h3>Projets sauvegardés</h3>
              {(() => {
                if (loading) return <div className="loading">Chargement...</div>;
                if (projects.length === 0) {
                  return (
                    <div className="empty-state">
                      <FileText size={32} />
                      <p>Aucun projet sauvegardé</p>
                    </div>
                  );
                }
                return (
                  <div className="projects-grid">
                    {projects.map((project) => (
                      <div key={project.id} className="project-card">
                        <div className="project-info">
                          {editingProject === project.id ? (
                          <div className="edit-name-form">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit(project.id)}
                              autoFocus
                            />
                            <button onClick={() => handleSaveEdit(project.id)} className="edit-confirm-btn" title="Valider">
                              <Check size={16} />
                            </button>
                            <button onClick={handleCancelEdit} className="edit-cancel-btn" title="Annuler">
                              <XCircle size={16} />
                            </button>
                          </div>
                        ) : (
                          <h4>{project.name}</h4>
                        )}
                        <span className="project-date">
                          <Clock size={14} />
                          {formatDate(project.updated_at)}
                        </span>
                      </div>
                      <div className="project-actions">
                        {editingProject !== project.id && (
                          <>
                            <button 
                              onClick={() => handleLoadProject(project)}
                              className="load-btn"
                              title="Charger ce projet"
                            >
                              <FolderOpen size={16} />
                              Charger
                            </button>
                            <button 
                              onClick={() => handleUpdateWithCurrentData(project.id, project.name)}
                              className="update-btn"
                              title="Mettre à jour avec les données actuelles"
                            >
                              <Save size={16} />
                            </button>
                            <button 
                              onClick={() => handleStartEdit(project)}
                              className="edit-btn"
                              title="Renommer"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button 
                              onClick={() => handleDeleteProject(project.id, project.name)}
                              className="delete-btn"
                              title="Supprimer"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </>
        )}

        {activeTab === 'exports' && (
          <div className="exports-list">
            <h3>Historique des exports PDF</h3>
            {exports.length === 0 ? (
              <div className="empty-state">
                <Download size={32} />
                <p>Aucun export effectué</p>
              </div>
            ) : (
              <div className="exports-table">
                <div className="table-header">
                  <span>Projet</span>
                  <span>Date</span>
                </div>
                {exports.map((exp) => (
                  <div key={exp.id || `${exp.project_id || exp.project_name || 'export'}-${exp.timestamp}`} className="table-row">
                    <span className="export-name">{exp.project_name || 'Export direct'}</span>
                    <span className="export-date">{formatDate(exp.timestamp)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsPanel;
